import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { AREA_COLOR_SAWS, GanttLineColors } from "@/shared/colors";
import {
	GroupedDelayAsset,
	getDelayAssetsGrouped,
} from "@/shared/repositories/DelayAssetRepository";
import { useUserScope } from "@/setup/scope";

const SAWS_AREA_NAME = "Saws";
// Only saws with a live production feed get a Home section (East/West/South are
// offline placeholders). Extend this list as more saws come online.
const TRACKED_SAW_NAMES = ["20th Street Saw"];
const SAW_COLORS: GanttLineColors = {
	main: AREA_COLOR_SAWS,
	light: "#BFE0E6",
	lighter: "#DCEFF3",
};

export interface SawLine {
	AssetId: number;
	AssetName: string;
	colors: GanttLineColors;
}

interface UseSawLinesProps {
	enabled?: boolean;
	onError?: (error: Error) => void;
}

export const useSawLines = ({
	enabled = true,
	onError,
}: UseSawLinesProps = {}) => {
	const { data, isLoading } = useQuery<GroupedDelayAsset[], Error>({
		queryKey: ["sawLineAssets"],
		queryFn: getDelayAssetsGrouped,
		enabled,
		staleTime: 5 * 60 * 1000,
		onError,
	});

	const scope = useUserScope();

	const sawLines = useMemo<SawLine[]>(() => {
		if (!data) return [];
		return data
			.filter(
				(a) =>
					a.ParentAssetName === SAWS_AREA_NAME &&
					TRACKED_SAW_NAMES.includes(a.AssetName) &&
					scope.includes(a.AssetId),
			)
			.sort((a, b) => a.AssetName.localeCompare(b.AssetName))
			.map((a) => ({
				AssetId: a.AssetId,
				AssetName: a.AssetName,
				colors: SAW_COLORS,
			}));
	}, [data, scope]);

	return { sawLines, isLoading };
};
